<template>
    <collapsable-table head-classes="cursor-pointer"
                       table-classes="w-full whitespace-no-wrap"
                       body-classes="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800 text-center">
        <template v-slot:head>
            <tr class="text-md font-bold tracking-wide text-center text-gray-700 border-b dark:border-gray-700 bg-gray-200 dark:text-gray-200 dark:bg-gray-700">
                <th class="px-4 py-3 w-2/5 text-left">
                    <h4 class="text-xl font-bold whitespace-normal">{{ section.name }}</h4>
                </th>
                <th class="px-4 py-3 w-2/5">
                    <div class="flex items-center w-full justify-center">
                        <div>
                            <p class="text-xl">
                                Average Score: {{ section.avgScore }}
                            </p>
                            <span
                                class="inline-block mt-3 px-2 py-1 font-semibold leading-tight text-lg text-orange-700 bg-orange-100 rounded-full dark:text-white dark:bg-orange-600"
                                :style="{
                                    backgroundColor: section.bgColor,
                                    color: section.textColor,
                                }">
                                {{ section.scoreRatingLabel }}
                            </span>
                        </div>
                    </div>
                </th>
                <th class="px-4 py-3 w-1/5">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mx-auto" fill="none"
                         viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M19 9l-7 7-7-7"></path>
                    </svg>
                </th>
            </tr>
        </template>
        <template v-slot:body>
            <tr class="text-gray-700 dark:text-gray-400" v-for="question in section.questions"
                :key="'detailed-question-' + question.id">
                <td class="px-4 py-3 w-2/5 text-sm text-left whitespace-normal">
                    <h4 class="text-lg font-semibold">{{ question.content }}</h4>
                    <collapsable v-if="question.allow_response_comment">
                        <template v-slot:trigger>
                            <button
                                class="mt-3 mb-2 p-4 text-md font-semibold flex items-center justify-between w-full rounded-md bg-gray-100 dark:bg-gray-600 focus:outline-none">
                                Comments
                                <span
                                    class="mr-auto ml-3 px-2 py-1 font-semibold leading-tight text-xs text-gray-700 bg-gray-300 rounded-full dark:text-gray-100 dark:bg-gray-700">
                                    {{ question.comments.length }}
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                          stroke-width="2"
                                          d="M19 9l-7 7-7-7"/>
                                </svg>
                            </button>
                        </template>
                        <template v-slot:content>
                            <div class="w-full py-4 px-6 bg-gray-100 dark:bg-gray-600 rounded-md">
                                <p v-if="question.response_comment_label" class="mb-2 font-bold">{{ question.response_comment_label }}</p>
                                <ul class="text-sm list-disc pl-4">
                                    <li v-for="response of question.comments" :key="'detailed-response-' + response.id">
                                        {{ response.comment }}
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </collapsable>
                </td>
                <td class="px-4 py-3 relative w-2/5">
                    <div v-if="!shouldRenderCharts">
                        <p class="font-semibold absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center z-10 text-gray-700 dark:text-gray-300">
                            Details will be available when the survey will close
                        </p>
                        <div class="blur-md" >
                            <div class="w-full">
                                <survey-chart style="height: 150px;" :chartData="mockData"></survey-chart>

                            </div>
                            <div class="text-sm text-center">
                                <p>Average Score: N/A</p>
                                <span
                                    class="inline-block mt-3 px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100">
                                    Label N/A
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div class="w-full">
                            <survey-chart style="height: 150px;"
                                          :chartData="extractChartQuestionData(question)"></survey-chart>
                        </div>
                        <div class="text-sm text-center">
                            <p>Average Score: {{ question.avgScore }}</p>
                            <span
                                class="inline-block mt-3 px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100"
                                :style="{
                                        backgroundColor: question.bgColor,
                                        color: question.textColor,
                                    }"
                            >
                              {{ question.label }}
                            </span>
                        </div>
                    </div>
                </td>
                <td class="px-4 py-3 text-sm text-left w-1/5">
                    <p class="my-1 align-middle flex items-center" v-for="answer in question.answers"
                       :key="'detailed-answer-' + answer.id">
                                    <span class="w-5 h-5 rounded-full inline-block mr-2"
                                          :style="{ backgroundColor: answer.bg_color }"></span> {{ answer.label }}
                    </p>
                </td>
            </tr>
        </template>
    </collapsable-table>

</template>

<script>
import Collapsable from '@/Components/Collapsable';
import CollapsableTable from '@/Components/CollapsableTable';
import SurveyChart from '@/Components/Survey/SurveyChart';
import {computed} from "vue";

export default {
    name: "SurveySectionDetails",
    components: {
        Collapsable,
        CollapsableTable,
        SurveyChart,
    },
    props: {
        section: {
            type: Object,
            required: true,
        },
        shouldRenderCharts: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const extractChartQuestionData = (question) => {
            const answers = question.answers.map((answer) => {
                return {
                    label: answer.label,
                    data: question.survey_responses.filter((response) => response.survey_question_answer_id === answer.id).length,
                    color: answer.bg_color,
                };
            });

            const datasets = [{
                label: question.content,
                data: answers.map((answer) => answer.data),
                backgroundColor: answers.map((answer) => answer.color),
            }];

            return {
                labels: answers.map((answer) => answer.label),
                datasets,
            };
        };

        const mockData = computed(() => ({
            labels: [
                "Very low",
                "Low",
                "Medium",
                "High",
                "Very high"
            ],
            datasets: [
                {
                    label: "Data not ready yet.",
                    data: Array(5).fill(Math.floor(Math.random() * 10)),
                    backgroundColor: [
                        "#d55353",
                        "#e17f23",
                        "#b09903",
                        "#7eb021",
                        "#0c6b00"
                    ]
                }
            ]
        }));

        return {
            mockData,
            extractChartQuestionData,
        };
    }
}
</script>
